// Helpers puros de fechas y rangos de los reportes de Recepción (sin React): los usan las tres
// vistas (simple, Pro, Históricas), el hook usePeriodoReportes y la exportación a PDF/Excel.

export const fechaISO = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const hoyISO = () => fechaISO(new Date());

/** "2026-09-13" → "13/09/2026". */
export const fechaCorta = (iso) => {
    const partes = String(iso || '').substring(0, 10).split('-');
    return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : '';
};

export const RANGOS_RAPIDOS = [
    { id: 'hoy', label: 'Hoy' },
    { id: 'semana', label: 'Últimos 7 días' },
    { id: 'mes', label: 'Este mes' },
    { id: 'anio', label: 'Este año' },
    { id: 'rango', label: 'Personalizado' },
];

/**
 * Rango "natural" de un preset, sin recortar: el mes y el año van completos. El recorte a hoy
 * (turnos futuros) lo aplica recortarRango, así el interruptor puede sumarlos sin recalcular.
 * Un preset desconocido (ej. 'todo') devuelve el rango vacío = todo el histórico.
 */
export function calcularRangoRapido(preset, dia = null) {
    const hoy = new Date();
    if (preset === 'hoy') return { desde: fechaISO(hoy), hasta: fechaISO(hoy) };
    if (preset === 'dia') {
        const elegido = dia || fechaISO(hoy);
        return { desde: elegido, hasta: elegido };
    }
    if (preset === 'semana') {
        const desde = new Date(hoy);
        desde.setDate(desde.getDate() - 6);
        return { desde: fechaISO(desde), hasta: fechaISO(hoy) };
    }
    if (preset === 'mes') {
        return {
            desde: fechaISO(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
            hasta: fechaISO(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0)),
        };
    }
    // "Últimos N meses" son N meses de CALENDARIO: del día 1 del mes N-1 hacia atrás hasta el
    // final del mes en curso. No se restan meses a la fecha de hoy a propósito: restarle 3 meses
    // a un 31 cae en un mes que no tiene 31 y JavaScript lo desborda al mes siguiente (31/05
    // menos 3 da 03/03). Con el día 1 no hay desborde posible. Los usa el reporte de turnos
    // ANSES, donde el ciclo es largo y mirar un mes suelto no dice nada.
    if (preset === 'trimestre' || preset === 'semestre') {
        const meses = preset === 'trimestre' ? 2 : 5;
        return {
            desde: fechaISO(new Date(hoy.getFullYear(), hoy.getMonth() - meses, 1)),
            hasta: fechaISO(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0)),
        };
    }
    if (preset === 'anio') {
        return { desde: fechaISO(new Date(hoy.getFullYear(), 0, 1)), hasta: fechaISO(new Date(hoy.getFullYear(), 11, 31)) };
    }
    return { desde: '', hasta: '' };
}

/** Los últimos 12 meses (del actual hacia atrás), para los chips que aparecen con "Mes". */
export function mesesRecientes() {
    const hoy = new Date();
    return Array.from({ length: 12 }, (_, i) => {
        const inicio = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
        const fin = new Date(inicio.getFullYear(), inicio.getMonth() + 1, 0);
        return {
            clave: `${inicio.getFullYear()}-${String(inicio.getMonth() + 1).padStart(2, '0')}`,
            etiqueta: `${inicio.toLocaleDateString('es-AR', { month: 'short' }).replace('.', '')} ${String(inicio.getFullYear()).slice(-2)}`,
            desde: fechaISO(inicio),
            hasta: fechaISO(fin),
        };
    });
}

/**
 * Sin turnos futuros, "hasta" nunca pasa de hoy: elegir el mes en curso, su chip o un rango que
 * termina más adelante contaba la agenda que todavía no ocurrió como si fueran turnos del período
 * (inflaba "agendados" y bajaba todas las tasas). Si el rango empieza en el futuro queda vacío.
 * @param {{desde: string, hasta: string}} rango
 * @param {boolean} incluirFuturos
 * @param {string} [hoy] ISO, para tests
 */
export function recortarRango({ desde, hasta }, incluirFuturos, hoy = hoyISO()) {
    if (incluirFuturos || !hasta) return { desde, hasta };
    if (hasta <= hoy) return { desde, hasta };
    if (desde && desde > hoy) return { desde, hasta: desde, vacio: true };
    return { desde, hasta: hoy, recortado: true };
}

/** Ventana previa de igual longitud, inmediatamente anterior a [desde, hasta]. */
export function calcularPeriodoAnterior(desde, hasta) {
    if (!desde || !hasta) return { desde: null, hasta: null };
    const d = new Date(`${desde}T00:00:00`);
    const h = new Date(`${hasta}T00:00:00`);
    const dias = Math.round((h - d) / 86400000) + 1;
    const prevHasta = new Date(d);
    prevHasta.setDate(prevHasta.getDate() - 1);
    const prevDesde = new Date(prevHasta);
    prevDesde.setDate(prevDesde.getDate() - (dias - 1));
    return { desde: fechaISO(prevDesde), hasta: fechaISO(prevHasta) };
}

/**
 * true si la ventana anterior empieza antes del primer turno que tiene el sistema: no hay contra qué
 * comparar (ese tramo está vacío, no en cero) y la variación saldría inflada.
 * @param {string} prevDesde ISO
 * @param {string|null} primerTurno ISO, viene de /resumen
 */
export function periodoAnteriorSinCobertura(prevDesde, primerTurno) {
    if (!prevDesde || !primerTurno) return false;
    return prevDesde < String(primerTurno).substring(0, 10);
}

/**
 * Aviso cuando el período pedido empieza antes del primer turno del sistema: lo anterior no entra en
 * estos indicadores (la planilla manual de mayo a agosto vive en Estadísticas históricas).
 * @returns {string|null}
 */
export function avisoPrimerTurno(desde, primerTurno) {
    if (!primerTurno) return null;
    const primero = String(primerTurno).substring(0, 10);
    if (desde && desde >= primero) return null;
    return `El sistema tiene turnos desde el ${fechaCorta(primero)}: lo anterior a esa fecha no entra en estos indicadores. Las atenciones de la planilla manual (mayo a agosto de 2026) están en Estadísticas históricas.`;
}

/** Todos los días entre desde y hasta (inclusive), en ISO. Vacío si el rango es inválido. */
export function diasDelRango(desde, hasta, maximo = 400) {
    if (!desde || !hasta || desde > hasta) return [];
    const dias = [];
    const cursor = new Date(`${desde}T00:00:00`);
    const fin = new Date(`${hasta}T00:00:00`);
    while (cursor <= fin && dias.length < maximo) {
        dias.push(fechaISO(cursor));
        cursor.setDate(cursor.getDate() + 1);
    }
    return dias;
}

export const esFinDeSemana = (iso) => {
    const dia = new Date(`${iso}T00:00:00`).getDay();
    return dia === 0 || dia === 6;
};
